import { CrossTabSessionChangedModal } from '@/components/cross-tab-session-changed-modal';
import { SoulMapSessionRefreshRegistration } from '@/components/soulmap-session-refresh-registration';
import { useCrossTabSessionNotice } from '@/hooks/useCrossTabSessionNotice';
import { AuthService } from '@/services/auth-service';
import { AUTH_EVENTS } from '@/services/utils/axios';
import { useCrossTabSessionNoticeStore } from '@/store/use-cross-tab-session-notice-store';
import { useAuth0 } from '@auth0/auth0-react';
import { NextUIProvider } from '@nextui-org/react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { GlobalSpinner } from 'Components/loader/GlobalSpinner';
import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { useEffect, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  Navigate,
  Route,
  Routes,
  useHref,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Error from 'Src/pages/error/error-page';
import SoulMapLoginPage from 'Src/pages/login/soulmap-login-page';
import { routeConfig } from 'Src/routes/route-config';
import { useTenantConfig } from './hooks/queries/tenant-config';
import { useActiveTenant } from './hooks/useActiveTenant';
import { useInputFocusTracker } from './hooks/useInputFocusTracker';
import { useSetUserInfo } from './hooks/useSetUserInfo';
import { formatAuth0CallbackError } from './hooks/useSsoLogin';
import { useViewportHeight } from './hooks/useViewportHeight';
import PrivateRoutes from './routes/private-routes';
import ProtectedRoute from './routes/protected-route';
import PublicRoutes from './routes/public-routes';
import { useAuthStore } from './store/use-auth-store';
import { useLoaderStore } from './store/use-loader-store';
import { resolveHomeTenantSlug } from './utils/active-tenant';
import { getSoulMapPostLoginPath, isSoulMapMode } from './utils/app-mode';
import {
  isAuth0RedirectCallback,
  isAuth0RedirectError,
  isTeamsContext,
} from './utils/auth-context';
import {
  clearBackendSessionValidated,
  markBackendSessionValidated,
} from './utils/auth-session';
import { getErrorMessage } from './utils/common';
import {
  clearPostLoginReturnPath,
  getPostLoginReturnPath,
} from './utils/post-login-return-path';
import {
  getAuth0LogoutReturnTo,
  getSoulMapConsumerLandingPath,
  isSoulMapLoginFlow,
  markPostLogoutLoginPath,
  markSoulMapSessionEstablished,
  POST_LOGOUT_LOGIN_PATH_KEY,
  shouldUseSoulMapConsumerLanding,
} from './utils/soulmap-auth';
import {
  clearTeamsSsoHandoff,
  clearTeamsSsoInProgress,
  clearTeamsSsoReloading,
  consumeActivationSsoReturnPath,
  consumeSsoSessionIntent,
  hasSsoSessionIntent,
  isActivateRoute,
  isLoginRoute,
  isPublicAuthFlowRoute,
  isTeamsSsoHandoff,
  isTeamsSsoInProgress,
  isTeamsSsoReloading,
  markActivationSsoError,
  readActivationSsoReturnPath,
  shouldAutoEstablishSsoSession,
} from './utils/sso-session-guard';
import {
  clearAuthQueryCache,
  hasAuth0UserChanged,
  markEstablishedAuth0Sub,
} from './utils/sync-auth-session';
import { isTeamsAuthPopupSession } from './utils/teams-auth';

function RootRedirect() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const tenants = useAuthStore((state) => state.tenants);
  const clearedPostLogoutRef = useRef(false);
  const postLogoutPath =
    typeof window !== 'undefined'
      ? sessionStorage.getItem(POST_LOGOUT_LOGIN_PATH_KEY)
      : null;
  const loginPath =
    postLogoutPath ??
    (isSoulMapMode() || shouldUseSoulMapConsumerLanding()
      ? getSoulMapConsumerLandingPath()
      : '/login');

  useEffect(() => {
    if (postLogoutPath && !clearedPostLogoutRef.current) {
      clearedPostLogoutRef.current = true;
      sessionStorage.removeItem(POST_LOGOUT_LOGIN_PATH_KEY);
    }
  }, [postLogoutPath]);

  if (isAuth0RedirectError()) {
    // Read only: the activation page clears it once it has mounted.
    const activationPath = readActivationSsoReturnPath();
    if (activationPath) {
      const params = new URLSearchParams(window.location.search);
      markActivationSsoError(
        formatAuth0CallbackError(
          params.get('error_description') || params.get('error') || ''
        )
      );
      return <Navigate to={activationPath} replace />;
    }

    return <Navigate to={`${loginPath}${window.location.search}`} replace />;
  }

  if (isAuth0RedirectCallback()) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-gray-900">
        <p className="text-sm text-gray-600">Completing sign in...</p>
      </div>
    );
  }

  // Soul Map hosts serve the landing at the root rather than redirecting to a path.
  if (isSoulMapMode()) {
    return <SoulMapLoginPage />;
  }

  if (isAuthenticated && shouldUseSoulMapConsumerLanding()) {
    return <Navigate to={getSoulMapConsumerLandingPath()} replace />;
  }

  // Signed-in users go straight to Home; /login would only bounce them back.
  // A pending post-logout path (failed invite SSO, Soul Map) still wins.
  if (isAuthenticated && tenants?.length && !postLogoutPath) {
    const homeTenantSlug = resolveHomeTenantSlug(tenants);
    if (homeTenantSlug) {
      return <Navigate to={`/${homeTenantSlug}/home`} replace />;
    }
    return (
      <Navigate to="/login" state={{ showTenantSelector: true }} replace />
    );
  }

  return <Navigate to={loginPath} replace />;
}

interface RouteConfig {
  path: string;
  element?: React.ReactNode;
  children?: RouteConfig[];
  isAdmin?: boolean;
  isPublic?: boolean;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isTeamsAuthPopupCallback =
    isTeamsAuthPopupSession() &&
    (location.pathname.includes('/teams-auth-start') ||
      isAuth0RedirectCallback());
  const isTeamsAuthCallbackOnRoot =
    isTeamsAuthPopupSession() &&
    !location.pathname.includes('/teams-auth-start');
  const {
    logout: logoutFromSSO,
    isAuthenticated: isAuthenticatedViaSSO,
    user,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  const queryClient = useQueryClient();
  const { setSession, setShowTenantSelector } = useAuthStore();
  const { tenantId } = useActiveTenant();
  const { setUserInfo } = useSetUserInfo();
  const { setIsLoading } = useLoaderStore();
  const ssoInitializedRef = useRef(false);
  const ssoEstablishInFlightRef = useRef(false);
  const staleAuth0CleanupRef = useRef(false);

  useTenantConfig(tenantId!);
  useCrossTabSessionNotice();

  // Track input focus globally for mobile keyboard detection
  useInputFocusTracker();

  // Set CSS custom property for true viewport height (mobile browser fix)
  useViewportHeight();

  useEffect(() => {
    if (
      isTeamsContext() &&
      isLoginRoute(location.pathname) &&
      (isTeamsSsoHandoff() || isTeamsSsoReloading())
    ) {
      setIsLoading(true);
    }
  }, [location.pathname, setIsLoading]);

  useEffect(() => {
    if (!isAuthenticatedViaSSO) return;
    const handleLogout = async () => {
      logoutFromSSO({
        logoutParams: {
          returnTo: getAuth0LogoutReturnTo(window.location.pathname),
        },
      });
    };

    window.addEventListener(AUTH_EVENTS.LOGOUT_REQUIRED, handleLogout);
    return () =>
      window.removeEventListener(AUTH_EVENTS.LOGOUT_REQUIRED, handleLogout);
  }, [isAuthenticatedViaSSO, logoutFromSSO]);

  useEffect(() => {
    if (!isAuthenticatedViaSSO) {
      ssoInitializedRef.current = false;
      ssoEstablishInFlightRef.current = false;
      staleAuth0CleanupRef.current = false;
      return;
    }

    if (!useAuthStore.getState().isAuthenticated) {
      ssoInitializedRef.current = false;
    }
  }, [isAuthenticatedViaSSO]);

  useEffect(() => {
    if (!isAuthenticatedViaSSO || !user || isTeamsAuthPopupCallback) {
      return;
    }

    const auth0UserChanged = hasAuth0UserChanged(user.sub);
    const hadSsoSessionIntent = hasSsoSessionIntent();

    if (ssoInitializedRef.current && !auth0UserChanged) {
      return;
    }

    // Extra effect runs (user hydrating, Auth0 callback identity) must not
    // treat an in-progress first login as a leftover Auth0 cookie.
    if (ssoEstablishInFlightRef.current) {
      return;
    }

    const initiatedLoginInThisTab =
      isAuth0RedirectCallback() || hadSsoSessionIntent;

    if (auth0UserChanged) {
      if (!initiatedLoginInThisTab) {
        // Cross-tab Auth0 switch — show modal only; full reload syncs state.
        if (useAuthStore.getState().isAuthenticated) {
          useCrossTabSessionNoticeStore
            .getState()
            .open('user_changed', user.email ?? null);
        }
        return;
      }

      clearAuthQueryCache(queryClient);
      clearBackendSessionValidated();
      ssoInitializedRef.current = false;
    }

    const onLoginRoute = isLoginRoute(location.pathname);
    const shouldEstablish = shouldAutoEstablishSsoSession({
      pathname: location.pathname,
      isAuth0RedirectCallback: isAuth0RedirectCallback(),
      isTeamsAuthPopupCallback,
    });

    if (!shouldEstablish) {
      if (
        (onLoginRoute || isPublicAuthFlowRoute(location.pathname)) &&
        !staleAuth0CleanupRef.current &&
        !hasSsoSessionIntent() &&
        !ssoEstablishInFlightRef.current &&
        !isTeamsSsoInProgress() &&
        !isTeamsSsoHandoff() &&
        !isTeamsSsoReloading()
      ) {
        staleAuth0CleanupRef.current = true;
        void logoutFromSSO({ openUrl: false });
      }
      return;
    }

    const initializeUser = async () => {
      let keepLoaderUntilRedirect = false;
      ssoEstablishInFlightRef.current = true;
      setIsLoading(true);

      try {
        const [accessToken, idToken] = await Promise.all([
          getAccessTokenSilently(),
          getIdTokenClaims(),
        ]);

        if (!idToken?.__raw) {
          throw new globalThis.Error('Missing Auth0 ID token');
        }

        await AuthService.establishSsoSession(accessToken, idToken.__raw);
        setSession('sso');
        consumeSsoSessionIntent();

        const userInfo = await setUserInfo();
        markBackendSessionValidated();
        consumeActivationSsoReturnPath();

        if (isSoulMapLoginFlow()) {
          markSoulMapSessionEstablished();
        }

        if (user.sub) {
          markEstablishedAuth0Sub(user.sub);
        }

        ssoInitializedRef.current = true;
        setShowTenantSelector(false);
        clearTeamsSsoInProgress();
        clearTeamsSsoHandoff();
        clearTeamsSsoReloading();

        // A reload re-establishes the backend session too; only a fresh sign-in
        // or an entry page should move the user. Direct loads of in-app URLs
        // (deep links, refresh) stay where they are, query string included.
        // Soul Map consumers keep being pulled back to their landing funnel.
        const onEntryRoute =
          location.pathname === '/' ||
          isLoginRoute(location.pathname) ||
          isPublicAuthFlowRoute(location.pathname);
        const shouldNavigateAfterLogin =
          initiatedLoginInThisTab || onEntryRoute || isSoulMapLoginFlow();

        if (shouldNavigateAfterLogin && userInfo?.tenant?.slug) {
          // A link saved for after login is LiFT-only; Soul Map sign-ins drop it.
          if (isSoulMapLoginFlow() || isSoulMapMode()) {
            clearPostLoginReturnPath();
          }

          const stayOnSoulMapLanding =
            isSoulMapLoginFlow() &&
            (location.pathname === '/soulmap-login' ||
              location.pathname.startsWith('/soulmap-login/'));

          if (!stayOnSoulMapLanding) {
            const destination = isSoulMapLoginFlow()
              ? getSoulMapConsumerLandingPath()
              : isSoulMapMode()
                ? getSoulMapPostLoginPath(userInfo.tenant.slug)
                : // Only a link in the tenant this session was set up for;
                  // its cohort scope is the one just loaded.
                  (getPostLoginReturnPath([userInfo.tenant]) ??
                  `/${userInfo.tenant.slug}/home`);

            navigate(destination, {
              replace: true,
            });
          }
        }
      } catch (error) {
        ssoInitializedRef.current = false;
        console.error('Error initializing user:', error);
        consumeSsoSessionIntent();

        clearTeamsSsoHandoff();
        clearTeamsSsoReloading();

        const message = isAxiosError(error)
          ? getErrorMessage(error.response?.data)
          : formatAuth0CallbackError(getErrorMessage(error));

        // After the Auth0 callback the router still reports "/" (the
        // callback's replaceState is invisible to it), so rely on the path
        // stored when activation SSO started rather than location.pathname.
        const activationPath =
          consumeActivationSsoReturnPath() ??
          (isActivateRoute(location.pathname)
            ? `${location.pathname}${location.search}`
            : null);

        if (activationPath) {
          staleAuth0CleanupRef.current = true;
          // Clear the Auth0 app session only — never federated, which signs
          // the user out of Google itself. Without this, Auth0 keeps the
          // rejected account and the next popup skips to Authorize.
          // Auth0 only returns to the origin; RootRedirect then sends the
          // user back to this invitation to retry or set a password.
          const logoutReturnTo = getAuth0LogoutReturnTo('/login', {
            markPostLogout: false,
          });
          // The activation page shows the error, so no toast here; keep the
          // spinner up while the logout round trip takes the user back there.
          markPostLogoutLoginPath(activationPath);
          markActivationSsoError(message);
          try {
            keepLoaderUntilRedirect = true;
            await logoutFromSSO({
              logoutParams: { returnTo: logoutReturnTo },
            });
          } catch (logoutError) {
            console.error('Failed to log out Auth0 session:', logoutError);
            keepLoaderUntilRedirect = false;
            navigate(activationPath, { replace: true });
          }
          return;
        }

        toast.error(message);

        if (onLoginRoute || isPublicAuthFlowRoute(location.pathname)) {
          staleAuth0CleanupRef.current = true;
          try {
            await logoutFromSSO({ openUrl: false });
          } catch (logoutError) {
            console.error('Failed to clear stale Auth0 session:', logoutError);
          }
        }

        navigate(isSoulMapLoginFlow() ? '/soulmap-login' : '/login');
      } finally {
        ssoEstablishInFlightRef.current = false;
        if (!keepLoaderUntilRedirect) {
          setIsLoading(false);
        }
      }
    };

    initializeUser();
  }, [
    isAuthenticatedViaSSO,
    user,
    isTeamsAuthPopupCallback,
    location.pathname,
    logoutFromSSO,
  ]);

  const isTeamsSsoHandoffScreen =
    isTeamsContext() &&
    isLoginRoute(location.pathname) &&
    (isTeamsSsoHandoff() || isTeamsSsoReloading());

  if (isTeamsAuthCallbackOnRoot || isTeamsSsoHandoffScreen) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-gray-900">
        <p className="text-sm text-gray-600">Completing sign in...</p>
      </div>
    );
  }

  const renderRoutes = (routes: RouteConfig[], isPublic: boolean) => {
    return routes.map(({ path, element, children, isAdmin }) => (
      <Route
        key={path}
        path={path}
        element={isAdmin ? <ProtectedRoute>{element}</ProtectedRoute> : element}
      >
        {children && renderRoutes(children, isPublic)}
      </Route>
    ));
  };

  return (
    <NextUIProvider navigate={navigate} useHref={useHref}>
      <SoulMapSessionRefreshRegistration />
      <CrossTabSessionChangedModal />
      <Sentry.ErrorBoundary fallback={<Error />}>
        <NextThemesProvider attribute="class" defaultTheme="light">
          <div className="min-h-screen bg-white dark:bg-gray-900">
            <Routes>
              {/* Public Routes */}
              <Route path="/">
                <Route element={<PublicRoutes />}>
                  {renderRoutes(
                    routeConfig.filter((route) => route.isPublic),
                    true
                  )}
                </Route>

                {/* Tenant Routes */}
                <Route path=":tenantSlug">
                  <Route element={<PrivateRoutes />}>
                    {renderRoutes(
                      routeConfig.filter((route) => !route.isPublic),
                      false
                    )}
                  </Route>
                </Route>

                {/* Root route - will be handled by PublicRoutes for redirection */}
                <Route index element={<RootRedirect />} />
              </Route>
            </Routes>
            <Toaster position="top-right" containerStyle={{ zIndex: 10001 }} />
            <GlobalSpinner />
          </div>
        </NextThemesProvider>
      </Sentry.ErrorBoundary>
    </NextUIProvider>
  );
}

export default App;
